/**
 * Small map helpers shared by the store, threat tracking and aiming: land lookup that
 * tolerates offshore points, nuke ring radii and human-readable distances / bearings.
 */
import { AXIAL_DIRS, axialOfPoint, getGeo, landAtAxial, mapToLonLat, type HexIdx, type WorldMap } from '@sa/sim';

const SQRT3 = Math.sqrt(3);
const KM_PER_DEG = 111.2;

/** Land hex at (x, y), searching a little around coasts so offshore targets still resolve. */
export function nearestLandHex(world: WorldMap, x: number, y: number): HexIdx {
  const g = getGeo(world);
  const [q0, r0] = axialOfPoint(g, x, y);
  const at = landAtAxial(g, q0, r0);
  if (at >= 0) return at;
  let best = -1;
  let bestD = (world.hexR * 1.2) ** 2;
  for (const [dq, dr] of AXIAL_DIRS) {
    const h = landAtAxial(g, q0 + dq, r0 + dr);
    if (h < 0) continue;
    const d = (world.cx[h] - x) ** 2 + (world.cy[h] - y) ** 2;
    if (d <= bestD) {
      bestD = d;
      best = h;
    }
  }
  return best;
}

/**
 * Outer radius (mu) of nuke ring k (0 = the impact hex, 1, 2): centre distance of the ring
 * plus the hex inradius. Ring 2 is the whole blast footprint.
 */
export function blastRingMu(world: Pick<WorldMap, 'hexR'>, k: number): number {
  return (k + 0.5) * SQRT3 * world.hexR;
}

/** Ground distance in km between two map points (equirectangular on the map projection). */
export function kmBetween(ax: number, ay: number, bx: number, by: number): number {
  const a = mapToLonLat(ax, ay);
  const b = mapToLonLat(bx, by);
  let dLon = b.lon - a.lon;
  if (dLon > 180) dLon -= 360;
  if (dLon < -180) dLon += 360;
  const lat = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  return Math.hypot(dLon * Math.cos(lat), b.lat - a.lat) * KM_PER_DEG;
}

const COMPASS = ['E', 'NE', 'N', 'NW', 'W', 'SW', 'S', 'SE'] as const;

/** 8-point compass bearing of (bx, by) as seen from (ax, ay); map y grows southwards. */
export function compass(ax: number, ay: number, bx: number, by: number): (typeof COMPASS)[number] {
  const a = Math.atan2(ay - by, bx - ax);
  const i = Math.round(a / (Math.PI / 4));
  return COMPASS[(i + 8) % 8];
}
